import type { FC } from 'react'
import type { Tile } from '@/types'

import { cn } from '@/lib/utils'

type BoardImageProps = {
    tile: Tile
    /** Overrides the tile's own description. Pass an empty string for a decorative image. */
    alt?: string
    className?: string
    /** Load straight away instead of when it nears the viewport. For above-the-fold images. */
    priority?: boolean
    [key: `data-${string}`]: string | number | boolean | undefined
}

/** `object-position` for a whole picture, per focus. */
const POSITION: Record<NonNullable<Tile['focus']>, string> = {
    center: 'object-center',
    top: 'object-top',
    bottom: 'object-bottom',
    left: 'object-left',
    right: 'object-right'
}

/** The SVG equivalent of `object-fit: cover` plus `object-position`, per focus. */
const FIT: Record<NonNullable<Tile['focus']>, string> = {
    center: 'xMidYMid slice',
    top: 'xMidYMin slice',
    bottom: 'xMidYMax slice',
    left: 'xMinYMid slice',
    right: 'xMaxYMid slice'
}

/**
 * Shows one project photograph, filling whatever box it is given like an
 * `<img>` with `object-cover`. A whole picture renders as a plain `<img>`; a
 * cut-out of a larger photo renders as an SVG whose viewBox is the crop, so
 * one high resolution photo can supply several individual images. Data
 * attributes pass straight through, so the animation hooks work on either.
 */
export const BoardImage: FC<BoardImageProps> = ({ tile, alt, className, priority = false, ...rest }) => {
    const label = alt ?? tile.alt
    const focus = tile.focus ?? 'center'

    if (!tile.crop) {
        return (
            <img
                {...rest}
                src={tile.src}
                alt={label}
                loading={priority ? 'eager' : 'lazy'}
                decoding='async'
                draggable={false}
                className={cn('block object-cover', POSITION[focus], className)}
            />
        )
    }

    const { width, height, x, y, w, h } = tile.crop

    return (
        <svg
            {...rest}
            viewBox={`${x} ${y} ${w} ${h}`}
            preserveAspectRatio={FIT[focus]}
            role={label ? 'img' : undefined}
            aria-label={label || undefined}
            aria-hidden={label ? undefined : true}
            focusable='false'
            className={cn('block overflow-hidden', className)}
        >
            <image href={tile.src} x='0' y='0' width={width} height={height} />
        </svg>
    )
}
